import React, { useState, useEffect } from 'react';
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaGlobe,
  FaLocationArrow,
  FaPlus,
  FaTimes,
  FaEdit,
  FaTrash,
  FaSearch,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function RegionID() {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    location: '',
    city: '',
  });
  const [searchQuery, setSearchQuery] = useState(''); // Stores search input
  const [editLocation, setEditLocation] = useState(null);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [showEditLocationForm, setShowEditLocationForm] = useState(false);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/properties`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property data. Status: ${response.status}`);
        }

        const data = await response.json();

        // Format the data to match your application's structure
        const formattedData = data.map((property) => ({
          hotel_details: {
            id: property.propertyid, // Use the `propertyid` from the API
            name: property.name,
            address: property.address,
            phone: property.phone,
            email: property.email,
            location: property.location,
            city: property.city,
          },
        }));

        setLocations(formattedData); // Update the state with formatted data
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching property data:', error);
        toast.error('Failed to load properties. Please try again later.');
      }
    };

    fetchHotels();
  }, []);

  const filteredLocations = locations.filter((location) =>
    location.hotel_details.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNewLocationChange = (e) => {
    const { name, value } = e.target;
    setNewLocation((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleEditLocationChange = (e) => {
    const { name, value } = e.target;
    setEditLocation((prevState) => ({
      ...prevState,
      hotel_details: { ...prevState.hotel_details, [name]: value },
    }));
  };

  const handleAddLocation = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          propertyname: newLocation.name, // Map `name` to `propertyname`
          city: newLocation.city,
          name: newLocation.name,
          location: newLocation.location,
          address: newLocation.address,
          phone: newLocation.phone,
          email: newLocation.email,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add the location. Please try again.');
      }

      const data = await response.json();

      setLocations([
        ...locations,
        {
          hotel_details: {
            id: data.propertyid, // Use `propertyid` from the API response
            name: data.name,
            address: data.address,
            phone: data.phone,
            email: data.email,
            location: data.location,
            city: data.city,
          },
        },
      ]);

      setShowAddLocationForm(false);
      setNewLocation({
        name: '',
        address: '',
        phone: '',
        email: '',
        location: '',
        city: '',
      });

      toast.success('Location added successfully!');
    } catch (error) {
      console.error('Error adding location:', error);
      toast.error('Something went wrong..!!');
    }
  };

  const handleEditLocation = async () => {
    try {
      const propertyid = editLocation?.hotel_details?.id; // Retrieve the property ID

      if (!propertyid) {
        toast.error('Please select a location with a valid property ID.');
        return;
      }

      // Construct the payload
      const payload = {
        propertyname: editLocation.hotel_details.name, // Map to propertyname
        city: editLocation.hotel_details.city,
        name: editLocation.hotel_details.name,
        location: editLocation.hotel_details.location,
        address: editLocation.hotel_details.address,
        phone: editLocation.hotel_details.phone,
        email: editLocation.hotel_details.email,
      };

      // Send the PUT request
      const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/${propertyid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(
          `Failed to update location. Status: ${response.status}. Message: ${errorText}`
        );
      }

      const updatedData = await response.json();

      // Update the local state with the new data
      const updatedLocations = locations.map((location) =>
        location.hotel_details.id === propertyid
          ? {
              ...location,
              hotel_details: {
                id: updatedData.propertyid,
                name: updatedData.name,
                address: updatedData.address,
                phone: updatedData.phone,
                email: updatedData.email,
                location: updatedData.location,
                city: updatedData.city,
              },
            }
          : location
      );

      setLocations(updatedLocations);
      setShowEditLocationForm(false);
      toast.success('Location updated successfully!');
    } catch (error) {
      console.error('Error editing location:', error);
      toast.error(error.message || 'An unexpected error occurred while updating the location.');
    }
  };

  const handleDeleteLocation = async (propertyid) => {
    try {
      if (!propertyid) {
        toast.error('Invalid property ID. Please select a valid location to delete.');
        return;
      }

      // Confirm deletion with the user
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this location? This action cannot be undone.'
      );
      if (!confirmDelete) return;

      // Send the DELETE request
      const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/${propertyid}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(
          `Failed to delete location. Status: ${response.status}. Message: ${errorText}`
        );
      }

      const deletedData = await response.json();
      console.log('Deleted data:', deletedData);

      // Update the local state to remove the deleted location
      setLocations(locations.filter((location) => location.hotel_details.id !== propertyid));

      toast.success('Location deleted successfully!');
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.message || 'An unexpected error occurred while deleting the location.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-50 p-8">
      <ToastContainer className="mt-15" />
      <div className="w-full max-w-full p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800">Explore Hotels by Region</h1>
        <p className="text-sm font-semibold text-center text-gray-500 mb-8">
          Admin can select and manage hotels by region to facilitate location-based hotel listings.
        </p>

        <div className=" flex flex-wrap items-center gap-2 text-sm">
          <div className="flex-grow relative">
            <div className="relative">
              <input
                id="hotelSearch"
                type="text"
                placeholder="Search hotels by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 pl-10 border border-gray-300 rounded-md shadow-sm "
              />
              <FaSearch size={16} className="absolute top-3.5 left-3 text-gray-500" />
            </div>
          </div>

          <div>
            <button
              onClick={() => setShowAddLocationForm(true)}
              className="flex items-center w-full px-9 py-3 bg-cyan-500 text-white rounded-md hover:text-black"
            >
              <FaPlus className="mr-2" /> Add Location
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mt-6">
          {isLoading ? (
            <div className="flex items-center  h-full">
              <div>
                <p className="text-2xl font-bold text-gray-500">Loading locations...</p>
              </div>
            </div>
          ) : (
            filteredLocations.map((location) => (
              <div className="bg-white shadow-md rounded-md p-6 cursor-pointer hover:border hover:border-black flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xl font-semibold text-gray-800">
                    {location.hotel_details.name}
                  </div>
                  <FaMapMarkerAlt className="text-gray-600 text-lg" />
                </div>

                <div className="text-sm font-semibold text-gray-600 mb-4">
                  {location.hotel_details.location}, {location.hotel_details.city}
                </div>

                <div className="text-sm text-gray-700 flex-grow">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaLocationArrow className="text-gray-600 text-lg" />
                    <p>
                      <strong>Address:</strong> {location.hotel_details.address}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <FaGlobe className="text-gray-600 text-lg" />
                    <p>
                      <strong>City:</strong> {location.hotel_details.city}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 mb-2">
                    <FaBuilding className="text-gray-600 text-lg" />
                    <p>
                      <strong>Phone:</strong> {location.hotel_details.phone}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <FaMapMarkerAlt className="text-gray-600 text-lg" />
                    <p>
                      <strong>Email:</strong> {location.hotel_details.email}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditLocation({
                        id: location.hotel_details.id, // Or use `hotelid` if available here
                        hotel_details: location.hotel_details,
                      });
                      setShowEditLocationForm(true);
                    }}
                    className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-md hover:text-black transition-all duration-300"
                  >
                    <FaEdit className="mr-2" /> Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLocation(location.hotel_details.id);
                    }}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:text-black"
                  >
                    <FaTrash className="mr-2" /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddLocationForm && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAddLocationForm(false)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              className="absolute top-3 right-4 text-red-500 hover:text-red-700"
              onClick={() => setShowAddLocationForm(false)}
            >
              <FaTimes className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="flex flex-col justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Add New Location</h2>
              <p className="text-sm font-medium text-gray-500">
                Allows administrators to effortlessly add new hotel details to the system.
              </p>
            </div>

            {/* Form */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <label htmlFor="hotelName" className="block text-sm font-medium text-gray-700 mb-1">
                  Hotel Name
                </label>
                <input
                  id="hotelName"
                  name="name"
                  type="text"
                  placeholder="Hotel Name"
                  value={newLocation.name}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="hotelAddress"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Hotel Address
                </label>
                <input
                  id="hotelAddress"
                  name="address"
                  type="text"
                  placeholder="Hotel Address"
                  value={newLocation.address}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  placeholder="Phone Number"
                  value={newLocation.phone}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="text"
                  placeholder="Email"
                  value={newLocation.email}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label htmlFor="landmark" className="block text-sm font-medium text-gray-700 mb-1">
                  Nearby Landmark
                </label>
                <input
                  id="landmark"
                  name="location"
                  type="text"
                  placeholder="Nearby Landmark"
                  value={newLocation.location}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  placeholder="City"
                  value={newLocation.city}
                  onChange={handleNewLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-sm"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-right mt-4">
              <button
                onClick={handleAddLocation}
                className="px-6 py-2 bg-cyan-500 text-white rounded-md hover:text-black "
              >
                Add Location
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for Editing Location */}
      {showEditLocationForm && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowEditLocationForm(false)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Edit Location</h2>

              <button onClick={() => setShowEditLocationForm(false)}>
                <FaTimes size={20} className="text-red-500 hover:text-red-700 text-xl" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <label
                  htmlFor="editHotelName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Hotel Name
                </label>
                <input
                  id="editHotelName"
                  name="name"
                  type="text"
                  placeholder="Hotel Name"
                  value={editLocation?.hotel_details.name || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label
                  htmlFor="editHotelAddress"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Hotel Address
                </label>
                <input
                  id="editHotelAddress"
                  name="address"
                  type="text"
                  placeholder="Hotel Address"
                  value={editLocation?.hotel_details.address || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label
                  htmlFor="editHotelPhone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number
                </label>
                <input
                  id="editHotelPhone"
                  name="phone"
                  type="text"
                  placeholder="Phone Number"
                  value={editLocation?.hotel_details.phone || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label
                  htmlFor="editHotelEmail"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  id="editHotelEmail"
                  name="email"
                  type="text"
                  placeholder="Email"
                  value={editLocation?.hotel_details.email || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label
                  htmlFor="editHotelLocation"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Nearby Landmark
                </label>
                <input
                  id="editHotelLocation"
                  name="location"
                  type="text"
                  placeholder="Nearby Landmark"
                  value={editLocation?.hotel_details.location || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label
                  htmlFor="editHotelCity"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  City
                </label>
                <input
                  id="editHotelCity"
                  name="city"
                  type="text"
                  placeholder="City"
                  value={editLocation?.hotel_details.city || ''}
                  onChange={handleEditLocationChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none "
                />
              </div>
            </div>
            <div className="text-right mt-4">
              <button
                onClick={handleEditLocation}
                className="px-6 py-2 bg-cyan-500 text-white rounded-md hover:text-black "
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegionID;
